import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Popover, Tag, Tooltip } from 'antd';
import { ApartmentOutlined, DollarOutlined, FilterOutlined, UnorderedListOutlined, UserOutlined } from '@ant-design/icons';
import { palette } from '../app/theme';

export type EntityKind = 'contact' | 'organization' | 'deal' | 'list' | 'segment';

const KINDS: Record<EntityKind, { path: string; icon: ReactNode; color: string }> = {
  contact: { path: '/contacts', icon: <UserOutlined />, color: 'blue' },
  organization: { path: '/organizations', icon: <ApartmentOutlined />, color: 'cyan' },
  deal: { path: '/deals', icon: <DollarOutlined />, color: 'gold' },
  list: { path: '/lists', icon: <UnorderedListOutlined />, color: 'geekblue' },
  segment: { path: '/segments', icon: <FilterOutlined />, color: 'purple' },
};

/**
 * A link to another record, drawn as a tag so it reads as clickable everywhere in the app.
 * `image` (an organization logo, say) replaces the kind's icon; `to` overrides the record's page;
 * `maxChars` cuts a longer text label with "..." (full text on hover).
 */
export function EntityTag({ kind, id, children, image, to, onClick, maxChars }: { kind: EntityKind; id: string; children: ReactNode; image?: string; to?: string; onClick?: () => void; maxChars?: number }) {
  const k = KINDS[kind];
  // Tag replaces its icon's style, so a logo goes in the content instead
  const logo = image && (
    <span style={{ display: 'inline-flex', marginInlineStart: -5, marginInlineEnd: 6, verticalAlign: 'middle', borderRadius: '50%', background: '#fff', border: `1px solid ${palette.borderStrong}` }}>
      <Avatar size={23} src={image} />
    </span>
  );
  const cut = maxChars && typeof children === 'string' && children.length > maxChars;
  const label = cut ? `${children.slice(0, maxChars).trimEnd()}...` : children;
  const link = (
    <Link to={to ?? `${k.path}/${id}`} onClick={onClick} style={{ display: 'inline-flex', maxWidth: '100%', verticalAlign: 'middle' }}>
      <Tag color={k.color} icon={logo ? undefined : k.icon} style={{ marginInlineEnd: 0, cursor: 'pointer', maxWidth: '100%', minWidth: 0, display: 'inline-flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap' }}>
        {logo}
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </Tag>
    </Link>
  );
  return cut ? <Tooltip title={children}>{link}</Tooltip> : link;
}

/** The first tag, then the second and a "+N" tag (hover lists the rest) on a line below; for table cells. */
export function TagGroup({ children, max = 2 }: { children: ReactNode[]; max?: number }) {
  const [first, ...more] = children.slice(0, max);
  const rest = children.slice(max);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, maxWidth: '100%' }}>
      {first}
      {(more.length > 0 || rest.length > 0) && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
          {more}
          {rest.length > 0 && (
            <Popover
              content={
                // clicks in the popover reach the table row through the portal; keep them from opening the row
                <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 320 }}>
                  {rest}
                </div>
              }
            >
              <Tag style={{ marginInlineEnd: 0, cursor: 'default' }}>+{rest.length}</Tag>
            </Popover>
          )}
        </span>
      )}
    </span>
  );
}
